import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { 
  LayoutDashboard, 
  Megaphone, 
  Palette, 
  Share2, 
  BarChart3, 
  Users, 
  Zap, 
  LineChart,
  Sparkles,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Bot,
  Sun,
  Moon
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/campaigns', icon: Megaphone, label: 'Campaigns' },
  { path: '/social-media', icon: Share2, label: 'Social Media' },
  { path: '/ai-assistant', icon: Bot, label: 'AI Assistant' },
  { path: '/content-studio', icon: Palette, label: 'AI Content Studio' },
  { path: '/ad-manager', icon: BarChart3, label: 'Ad Manager' },
  { path: '/leads-crm', icon: Users, label: 'Leads & CRM' },
  { path: '/automation', icon: Zap, label: 'Automation' },
  { path: '/analytics', icon: LineChart, label: 'Analytics' },
  { path: '/brand-management', icon: Sparkles, label: 'Brand Management' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-card border border-border"
      >
        {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 left-0 h-screen z-40
        ${sidebarOpen ? 'w-64' : 'w-20'} 
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        bg-card border-r border-border transition-all duration-300
        flex flex-col
      `}>
        {/* Logo */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-20 h-10 rounded-xl flex items-center justify-center shrink-0">
              {/* <Bot className="w-5 h-5 text-primary-foreground" /> */}
              <div className="w-56 sm:w-64 md:w-72 lg:w-80 h-24 sm:h-28 md:h-32 flex items-center justify-center overflow-visible transition-transform duration-300 group-hover:scale-105">

  <img
    src="/logoai.png"
    alt="Logo"
    className="w-full h-full object-contain scale-125"
  />

</div>
            </div>
            {sidebarOpen && (
              <div className="animate-fade-in">
                <h1 className="font-bold text-sm">AI Marketing</h1>
                <p className="text-xs text-muted-foreground">Automation</p>
              </div>
            )}
          </div>
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-card border border-border rounded-full items-center justify-center text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className={`w-4 h-4 transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <p className={`text-xs text-muted-foreground uppercase tracking-wider mb-2 ${sidebarOpen ? 'px-3' : 'text-center'}`}>
            {sidebarOpen ? 'Main Menu' : '•'}
          </p>
          
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) => `
                marketing-nav-item ${isActive ? 'active' : ''}
                ${!sidebarOpen ? 'justify-center px-2' : ''}
              `}
            >
              <item.icon className="w-5 h-5 shrink-0" />
              {sidebarOpen && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-3 border-t border-border">
          <div className={`flex items-center gap-3 ${!sidebarOpen ? 'justify-center' : ''}`}>
            <div className="w-9 h-9 ai-gradient rounded-lg flex items-center justify-center text-primary-foreground font-bold text-sm shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0 animate-fade-in">
                <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email || ''}</p>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            className={`
              mt-3 w-full marketing-nav-item text-red-400 hover:text-red-300 hover:bg-red-500/10
              ${!sidebarOpen ? 'justify-center px-2' : ''}
            `}
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen bg-background">
        {/* Header with Theme Toggle */}
        <header className="sticky top-0 z-20 bg-background border-b border-border">
          <div className="flex items-center justify-end px-6 lg:px-8 py-3">
            <button
              onClick={toggleTheme}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary hover:bg-secondary/80 border border-border transition-all duration-300"
              data-testid="theme-toggle-btn"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? (
                <>
                  <Sun className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Light Mode</span>
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Dark Mode</span>
                </>
              )}
            </button>
          </div>
        </header>
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
